// Traceroute hops labelled with the network (AS) that answered, as the
// master stores them (docs/adr/2026-10-04-as-path-monitoring.md §1–§2).

export type HopClass = "public" | "private" | "unknown";

export type TraceHop = {
  ttl: number;
  address: string;
  rtt_avg_ms: number;
  loss_pct: number;
  class?: HopClass;
  asn?: number;
  as_name?: string;
  as_country?: string;
};

export type PathAS = { asn: number; name: string; country?: string };

/** A run of consecutive hops answered from the same AS (or address class). */
export type HopGroup = { key: string; label: string; title: string; hops: TraceHop[] };

/** The handle of an AS name: "EXAMPLE-NET Example Networks Ltd" → "EXAMPLE-NET". */
export function asHandle(name?: string): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

const CLASS_LABEL: Record<Exclude<HopClass, "public">, string> = {
  private: "Private or special-use addresses",
  unknown: "Not announced by any AS",
};

function groupOf(h: TraceHop): Omit<HopGroup, "hops"> | null {
  if (!h.address || !h.class) return null;
  if (h.class !== "public") return { key: h.class, label: CLASS_LABEL[h.class], title: CLASS_LABEL[h.class] };
  const handle = asHandle(h.as_name);
  return {
    key: `AS${h.asn}`,
    label: [`AS${h.asn}`, handle, h.as_country].filter(Boolean).join(" · "),
    title: [`AS${h.asn}`, h.as_name, h.as_country].filter(Boolean).join(" · "),
  };
}

/**
 * Splits hops into runs answered by the same AS. Silent hops stay in the run
 * before them; silent hops before the first answer form a group with an
 * empty key (no header). Unlabelled results come back as a single group.
 */
export function groupHops(hops: TraceHop[]): HopGroup[] {
  const groups: HopGroup[] = [];
  for (const h of hops) {
    const g = groupOf(h);
    const last = groups[groups.length - 1];
    if (last && (g === null || g.key === last.key)) {
      last.hops.push(h);
    } else {
      groups.push({ ...(g ?? { key: "", label: "", title: "" }), hops: [h] });
    }
  }
  return groups;
}

export type TraceStop = "reached" | "unreachable" | "gap_limit" | "max_hops" | "cancelled";

/** Where the trace aimed, as the master labelled it. */
export type TraceTarget = {
  address: string;
  reached: boolean;
  answered?: boolean;
  rtt_avg_ms?: number;
  class?: HopClass;
  asn?: number;
  as_name?: string;
  as_country?: string;
};

/** The target's network in words: "AS64496 EXAMPLE-ONE", or its address class. */
export function targetNetwork(t: TraceTarget): string {
  if (t.class === "public") return [`AS${t.asn}`, asHandle(t.as_name)].filter(Boolean).join(" ");
  if (t.class === "private") return "a private or special-use address";
  if (t.class === "unknown") return "not announced by any AS";
  return "";
}

/**
 * How the trace ended, in one sentence. Results from probes older than
 * 0.1.1-dev.7 carry no stop reason and no target; they get none.
 */
export function traceOutcome(stop: TraceStop | undefined, target: TraceTarget | undefined, hops: TraceHop[], rttText: (ms: number) => string): string {
  const last = hops[hops.length - 1]?.ttl;
  switch (stop) {
    case "reached":
      return `Reached the target at hop ${last}.`;
    case "unreachable":
      return `A router at hop ${last} reported the target unreachable.`;
    case "gap_limit":
      return target?.answered
        ? `Stopped after 5 silent hops in a row, but the target answers (${rttText(target.rtt_avg_ms ?? 0)}): the networks in between did not reply.`
        : "Stopped after 5 silent hops in a row; the target did not answer either.";
    case "max_hops":
      return target?.answered
        ? `Did not reach the target within ${last} hops, but it answers (${rttText(target.rtt_avg_ms ?? 0)}): it is further away.`
        : `Did not reach the target within ${last} hops; it did not answer either.`;
    case "cancelled":
      return `Cut off by the probe's time limit after hop ${last ?? 0}.`;
    default:
      return "";
  }
}

/** ASNs typed as "2906, AS40027": their numbers, or null when one is not an ASN. */
export function asnList(s: string): number[] | null {
  const out: number[] = [];
  for (const x of s.split(/[\s,]+/).filter(Boolean)) {
    const m = /^(?:AS)?(\d{1,10})$/i.exec(x);
    if (!m || Number(m[1]) < 1 || Number(m[1]) > 4294967295) return null;
    out.push(Number(m[1]));
  }
  return out;
}
